import React, { useMemo, useState } from 'react';
import { Form, Badge } from 'react-bootstrap';
import { Table } from '@rd-web-markets/shared/dist/util';
import { EditButton, DeleteButton, SaveButton, CancelButton } from '@rd-web-markets/shared/dist/util/buttons';
import { useTranslation } from 'react-i18next';

const AllowedCharactersTable = ({ characterReplacements = [], onUpdate, onDelete, onFilter, userCanEdit, loading }) => {
  const { t } = useTranslation();
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({});

  const handleEdit = (characterReplacement) => {
    setEditingId(characterReplacement.id);
    setFormData({
      invalid_character: characterReplacement.invalid_character || '',
      replacement_character: characterReplacement.replacement_character || '',
      description: characterReplacement.description || ''
    });
  };

  const handleCancel = () => {
    setEditingId(null);
    setFormData({});
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async (characterReplacement) => {
    setSaving(true);
    await onUpdate({ id: characterReplacement.id, data: formData });
    setEditingId(null);
    setFormData({});
    setSaving(false);
  };

  const handleDelete = async (characterReplacement) => {
    if (window.confirm(t('confirm_delete'))) {
      await onDelete(characterReplacement.id);
    }
  };

  const canSave = formData.invalid_character?.trim() !== '';

  const headers = useMemo(() => [
    {
      text: t('invalid_character'),
      width: '15%',
      sortable: false,
      filterableByText: true,
      filterBy: 'invalid_character',
      styleAttributes: { paddingBottom: '45px' }
    },
    {
      text: t('replacement_character'),
      width: '15%',
      sortable: false,
      filterableByText: true,
      filterBy: 'replacement_character',
      styleAttributes: { paddingBottom: '45px' }
    },
    {
      text: t('description'),
      width: '50%',
      sortable: false,
      filterableByText: true,
      filterBy: 'description',
      styleAttributes: { paddingBottom: '45px' }
    },
    { text: '', width: '20%' }
  ], [t]);

  const rows = useMemo(() => {
    return characterReplacements.map((cr) => {
      const isEditing = editingId === cr.id;
      const isSavingThisRow = saving && isEditing;

      const columns = isEditing ? [
        <Form.Control
          size="sm"
          value={formData.invalid_character}
          onChange={(e) => handleChange('invalid_character', e.target.value)}
          placeholder={t('invalid_character')}
        />,
        <Form.Control
          size="sm"
          value={formData.replacement_character}
          onChange={(e) => handleChange('replacement_character', e.target.value)}
          placeholder={t('replacement_character_optional')}
        />,
        <Form.Control
          size="sm"
          value={formData.description}
          onChange={(e) => handleChange('description', e.target.value)}
          placeholder={t('description')}
        />,
        <div className="d-flex justify-content-end gap-2">
          <SaveButton
            size="sm"
            onClick={() => handleSave(cr)}
            disabled={isSavingThisRow || !canSave}
            text={isSavingThisRow ? t('saving') : t('save')}
          />
          <CancelButton
            size="sm"
            onClick={handleCancel}
            disabled={isSavingThisRow}
          />
        </div>
      ] : [
        <Badge bg="secondary"><code>{cr.invalid_character}</code></Badge>,
        <small>{cr.replacement_character || <span className="text-muted">{t('none')}</span>}</small>,
        <small>{cr.description}</small>,
        userCanEdit ? (
          <div className="d-flex justify-content-end gap-2">
            <EditButton size="sm" onClick={() => handleEdit(cr)} />
            <DeleteButton size="sm" onClick={() => handleDelete(cr)} />
          </div>
        ) : null
      ];

      return {
        key: `${cr.id}-${isEditing ? 'edit' : 'view'}`,
        columns
      };
    });
  }, [characterReplacements, editingId, formData, userCanEdit, saving, t]);

  return (
    <Table
      headers={headers}
      rows={rows}
      showControls={false}
      onFilter={onFilter}
      textFilterQueryStringParamName='query'
      headerLowerItemStyle={{ position: 'absolute', bottom: 10 }}
      opts={{
        bordered: false,
        striped: true,
        responsive: true,
        hover: true,
        headerSticky: true,
        headerClass: 'allowed-characters-table-header'
      }}
    />
  );
};

export default AllowedCharactersTable;
